/* ============================================================
   POPUP CLAIM CIRI PERCUMA PRO STUDIO (satu ciri setiap popup)
   ============================================================
   Keputusan user 2026-09-26: claim IKUT CIRI. Popup muncul bila
   pelanggan membuka halaman ciri itu di PRO Studio dan belum claim;
   juga dibuka dari pautan "Aktifkan" (Control Panel CTA, "/" di
   Workspace, My Plan). "Nanti dulu" = sorok untuk hari ini sahaja.

   Tajuk ditetapkan user: 'Cuba features "Shortcut /" secara percuma'
   dan 'Cuba features "CTA Beg Kuning" secara percuma'.
   ============================================================ */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Gift, Sparkles, CalendarClock } from 'lucide-react';

import { Btn } from '../design/ui';
import { CIRI, tuntutCiri, tandaNanti } from '../utils/ciriPercuma';

const TEKS = {
    [CIRI.SHORTCUT]: {
        tajuk: ['ciri.shortcut.popupTitle', 'Cuba features "Shortcut /" secara percuma'],
        isi: ['ciri.shortcut.popupBody', 'Simpan prompt yang kerap anda guna, kemudian taip "/" dalam Edit Prompts untuk memasukkannya serta-merta.'],
    },
    [CIRI.CTA]: {
        tajuk: ['ciri.cta.popupTitle', 'Cuba features "CTA Beg Kuning" secara percuma'],
        isi: ['ciri.cta.popupBody', 'Guna ayat anda sendiri pada beg kuning TikTok dan beg produk Shopee di Facebook Reels, bukan lagi "BELI KAT SINI". Pilih ayat CTA yang anda mahu gunakan di Control Panel.'],
    },
};

export default function PopupCiriPercuma({ ciri, onTutup, onBerjaya, t }) {
    const [sibuk, setSibuk] = useState(false);
    const [ralat, setRalat] = useState('');

    const T = (kunci, lalai) => {
        if (!t) return lalai;
        const hasil = t(kunci);
        return (!hasil || hasil === kunci) ? lalai : hasil;
    };
    const teks = TEKS[ciri];
    if (!teks) return null;

    const claim = async () => {
        setSibuk(true); setRalat('');
        try {
            await tuntutCiri(ciri);
            onBerjaya?.();
            onTutup?.();
        } catch (e) {
            console.warn('[CiriPercuma] claim gagal:', e?.code, e?.message);
            setRalat(e?.message === 'tamat'
                ? T('ciri.err.tamat', 'Tempoh percuma sudah tamat.')
                : T('ciri.err.umum', 'Tidak dapat mengaktifkan sekarang. Sila cuba lagi.'));
        } finally { setSibuk(false); }
    };
    const nanti = () => { tandaNanti(ciri); onTutup?.(); };

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            onClick={nanti}
            style={{
                position: 'fixed', inset: 0, zIndex: 10080, background: 'rgba(0,0,0,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: 460, borderRadius: 14, padding: '22px 24px',
                    background: 'var(--s-card, #ffffff)', color: 'var(--s-ink, #1f1e1b)',
                    border: '1px solid var(--s-line, #e5e3dc)', boxShadow: '0 16px 40px rgba(0,0,0,0.25)',
                }}
            >
                {/* Kepala: ikon hadiah + lencana percuma (arahan user 26 Sept). */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{
                        width: 44, height: 44, flex: 'none', borderRadius: 12,
                        display: 'grid', placeItems: 'center',
                        background: 'var(--s-ok-soft, #e8f3de)', color: 'var(--s-ok, #3b6d11)',
                    }}>
                        <Gift size={22} strokeWidth={1.9} />
                    </span>
                    <span style={{
                        fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 99,
                        background: 'var(--s-ok-soft, #e8f3de)', color: 'var(--s-ok, #3b6d11)',
                    }}>
                        {T('ciri.badgeGift', 'Percuma untuk anda')}
                    </span>
                </div>

                <h3 style={{ margin: '16px 0 6px', fontSize: 18, fontWeight: 600, lineHeight: 1.35 }}>{T(...teks.tajuk)}</h3>
                <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: 1.6, color: 'var(--s-ink-2, #55534c)' }}>{T(...teks.isi)}</p>

                {/* Kotak maklumat penting — dipisahkan daripada ayat penerangan
                    supaya "berbayar" + "tarikh tamat" tidak tenggelam. */}
                <div style={{
                    display: 'grid', gap: 12, padding: '12px 14px', marginBottom: 18, borderRadius: 10,
                    background: 'var(--s-fill, #f5f4ef)', border: '1px solid var(--s-line, #e5e3dc)',
                }}>
                    {[
                        [<Sparkles key="i" size={16} strokeWidth={1.9} style={{ flex: 'none', marginTop: 2, color: 'var(--s-ink-2, #55534c)' }} />, T('ciri.box.proTitle', 'Sebahagian daripada PRO Studio'), T('ciri.box.proBody', 'Pakej tambahan berbayar yang sedang kami siapkan.')],
                        [<CalendarClock key="i" size={16} strokeWidth={1.9} style={{ flex: 'none', marginTop: 2, color: 'var(--s-ink-2, #55534c)' }} />, T('ciri.box.dateTitle', 'Percuma sehingga 30 November 2026'), T('ciri.box.dateBody', 'Selepas tarikh ini, ciri ini memerlukan pakej PRO Studio.')],
                    ].map(([ikon, tajuk, isi]) => (
                        <div key={tajuk} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                            {ikon}
                            <div>
                                <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--s-ink, #1f1e1b)' }}>{tajuk}</div>
                                <div style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--s-ink-3, #8a877f)', marginTop: 1 }}>{isi}</div>
                            </div>
                        </div>
                    ))}
                </div>
                {ralat && <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--s-stop, #a32d2d)' }}>{ralat}</p>}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Btn variant="ghost" onClick={nanti} disabled={sibuk}>{T('ciri.later', 'Nanti dulu')}</Btn>
                    <Btn variant="primary" onClick={claim} disabled={sibuk}>
                        {sibuk ? T('ciri.claiming', 'Mengaktifkan…') : T('ciri.claim', 'Saya faham, aktifkan percuma')}
                    </Btn>
                </div>
            </div>
        </div>,
        document.body
    );
}
